/**
 * Card projections (A-5): schema→DATA resolution shared by every surface.
 * The web keeps schema→React registration as an enhancement on top; a text
 * client renders these projections directly.
 */

import { extractEntities, type ExtractEntitiesOptions } from "./extractEntities.ts";

/**
 * Flattens a tool-kind envelope to the legacy card data shape so entity
 * renderers can read fields directly (e.g. `data.name`).
 *
 * - `{kind:"created", id, schema_id, fields}` → `{id, schema_id, ...fields}`
 * - `{kind:"updated", id, schema_id, changed}` → `{id, schema_id, ...after-values}`
 * - anything else passes through unchanged.
 *
 * Envelope is persisted verbatim in chat history so cards render the same
 * forever — no graph fetches at render time. (Moved verbatim from
 * `frontend/src/components/agent/resolveCardFields.ts`; the web file is a
 * re-export facade.)
 */
export function resolveCardFields(
  data: Readonly<Record<string, unknown>>,
): Readonly<Record<string, unknown>> {
  const kind = data.kind;
  if (kind === "created") {
    const fields = data.fields;
    if (fields && typeof fields === "object" && !Array.isArray(fields)) {
      return { ...(fields as Record<string, unknown>), id: data.id, schema_id: data.schema_id };
    }
    return data;
  }
  if (kind === "updated") {
    const changed = data.changed;
    if (changed && typeof changed === "object" && !Array.isArray(changed)) {
      const flat: Record<string, unknown> = { id: data.id, schema_id: data.schema_id };
      for (const [key, entry] of Object.entries(changed as Record<string, unknown>)) {
        if (entry && typeof entry === "object" && "after" in (entry as Record<string, unknown>)) {
          flat[key] = (entry as Record<string, unknown>).after;
        }
      }
      return flat;
    }
    return data;
  }
  return data;
}

/** Renderable card data — what any surface needs to draw an entity card.
 *  `title` follows the web's BaseEntityCard rule: name → title → "Untitled". */
export interface EntityCardProjection {
  readonly schemaId: string;
  readonly entityId: string | null;
  readonly title: string;
  readonly kind: "created" | "updated" | null;
  /** Flattened field values (resolveCardFields output). */
  readonly fields: Readonly<Record<string, unknown>>;
}

/** Extract + flatten a tool result into renderable card projections —
 *  extractEntities ∘ resolveCardFields, with the web's title rule. */
export function projectEntityCards(
  result: unknown,
  opts?: ExtractEntitiesOptions,
): readonly EntityCardProjection[] {
  return extractEntities(result, opts).map((entity) => {
    const kind =
      entity.kind === "created" || entity.kind === "updated" ? entity.kind : null;
    const flat = resolveCardFields(entity);
    const name = typeof flat.name === "string" && flat.name.length > 0 ? flat.name : null;
    const title = typeof flat.title === "string" && flat.title.length > 0 ? flat.title : null;
    return {
      schemaId: typeof flat.schema_id === "string" ? flat.schema_id : "",
      entityId: flat.id != null ? String(flat.id) : null,
      title: name ?? title ?? "Untitled",
      kind,
      fields: flat,
    };
  });
}
